import { App } from 'antd';
import get from 'lodash-es/get';
import { useState } from 'react';

import { connectionsActions } from '@store/connections/connections.slice';
import { integrationsActions } from '@store/integrations/integrations.slice';

import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { IntegrationStateEnum } from '@enum/integration-state.enum';
import { UnifiedApiEnum } from '@enum/unified-api.enum';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useLazyQuery } from '@hook/common/use-lazy-query.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useDeleteConnectionMutation, useLazyLoadConnectionsQuery } from '@hook/connection/connection.hooks';
import { useLazyLoadIntegrationsQuery } from '@hook/intergration/integration.hooks';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ConnectionInterface, ConnectionStateEnum } from '@interface/connection/connection.interface';

export const useIntegrationCard = (connection: ConnectionInterface, currentState: ConnectionStateEnum) => {
  const { message } = App.useApp();
  const { current } = useCurrentApplication();
  const connectors = useStoreSelector((state) => state.connectors.data);
  const [serviceId, setServiceId] = useState<ConnectorServiceEnum | undefined>(undefined);
  const integrations = useStoreSelector((state) => state.integrations.data);
  const { action, state } = useMutation(useDeleteConnectionMutation(), connectionsActions);
  const {
    action: actionConnection,
    state: { isLoading: isLoadingConnection },
  } = useLazyQuery(
    useLazyLoadConnectionsQuery({ refetchOnReconnect: true, refetchOnFocus: true }),
    connectionsActions,
    connectionsActions.setData,
  );
  const {
    action: actionIntegration,
    state: { isLoading: isLoadingIntegrations },
  } = useLazyQuery(
    useLazyLoadIntegrationsQuery({ refetchOnReconnect: true, refetchOnFocus: true }),
    integrationsActions,
    integrationsActions.setData,
  );

  const connector = connectors[connection.serviceId];
  const onOpenModal = (currentServiceId: ConnectorServiceEnum | undefined) => () => setServiceId(currentServiceId);
  const removeConnection = () =>
    action({ unifiedApi: connection.unifiedApi as UnifiedApiEnum, serviceId: connection.serviceId }).then(() => {
      message.success('Integration was reset successfully');
      actionIntegration(current.id, false).then(() => actionConnection(undefined, false));
    });

  const isIntegrationDisabled = get(integrations, [connection.serviceId, 'state']) === IntegrationStateEnum.DISABLED;
  const isConnectionAvailable =
    connection.state === ConnectionStateEnum.Available || connection.state === ConnectionStateEnum.Added;
  const isConnected =
    connection.state === ConnectionStateEnum.Callable || connection.state === ConnectionStateEnum.Authorized;

  const isShowed =
    currentState === null ||
    (ConnectionStateEnum.Added === currentState && isConnected) ||
    (ConnectionStateEnum.Available === currentState && isConnectionAvailable && !isIntegrationDisabled) ||
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (currentState === IntegrationStateEnum.DISABLED && isIntegrationDisabled);

  return {
    isConnected,
    description: get(connector, 'description'),
    name: get(connector, 'name'),
    isShowed,
    serviceId,
    onOpenModal,
    integration: integrations[connection.serviceId],
    removeConnection,
    isLoading: state.isLoading || isLoadingConnection || isLoadingIntegrations,
  };
};
